import React, { useState, useCallback, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Plus,
  Zap,
  ExternalLink,
  Save,
  ClipboardCopy,
  Check as CheckIcon,
} from 'lucide-react';

import { cn } from '../lib/utils';
import {
  ScopeGenerationResponse,
  ScopeResponseRoom,
  RoomEntry,
  RoomScopeRequest,
  ValidationIssue,
  StructuredAddress,
} from '../types';
import { BackendAPI } from '../lib/api';
import { streamScopeGeneration } from '../lib/scopeStream';
import { useSessionState } from '../hooks/useSessionState';
import useProjectStore from '../stores/projectStore';

import { Button } from '../components/ui/Button';
import { GenerationProgress } from '../components/scope/GenerationProgress';
import { DamageTypeSelector } from '../components/scope/DamageTypeSelector';
import { RoomInputPanel } from '../components/scope/RoomInputPanel';
import { HelpfulModeChecklist } from '../components/scope/HelpfulModeChecklist';
import { ScopeOutput } from '../components/scope/ScopeOutput';
import { PipelineTracePanel } from '../components/scope/PipelineTracePanel';
import { scopeToMarkdown } from '../components/scope/scopeMarkdown';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { CreateProjectModal } from '../components/layout/ProjectContextHeader';
import { useIsAdmin } from '../hooks/useIsAdmin';

// ================================================================= //
//          Scope Builder Page — Interactive Hybrid Flow               //
// ================================================================= //
// Single-page scope builder: estimator selects damage type, adds     //
// rooms with descriptions, generates scope per-room or batch, and    //
// sees results inline with list/card view. No navigation away.       //
// Address autocomplete pre-fills ZIP for jurisdiction lookup.         //
// When project context exists, auto-saves generated scopes.          //
// ================================================================= //

/**
 * Generate a unique ID for room entries.
 */
function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `room-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function ScopeBuilderPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('project');

  // Reason: Persist primary page state across tab switches (Scope Builder ↔
  // Supplement ↔ etc.) so generated scopes and in-progress inputs survive
  // unmount. Keyed by projectId. When no project is selected, keys are
  // null and useSessionState behaves as in-memory state — so an exploratory
  // build doesn't pin itself to a shared bucket that haunts later visits.
  const scopeKey = projectId ? `scopeBuilder:scope:${projectId}` : null;
  const roomsKey = projectId ? `scopeBuilder:rooms:${projectId}` : null;
  const configKey = projectId ? `scopeBuilder:config:${projectId}` : null;

  // --- Configuration state (persisted) ---
  const [config, setConfig] = useSessionState(configKey, {
    damageType: '',
    claimType: '',
    jurisdiction: '',
    helpfulMode: false,
    cat3Toggle: false,
    address: '',
    houseDetails: '',  // S3: overall-house box
  });
  const { damageType, claimType, jurisdiction, helpfulMode, cat3Toggle, address, houseDetails } = config;
  const setHouseDetails = useCallback(
    (v: string) => setConfig((prev) => ({ ...prev, houseDetails: v })),
    [setConfig],
  );
  const setDamageType = useCallback(
    (v: string) => setConfig((prev) => ({ ...prev, damageType: v })),
    [setConfig],
  );
  const setClaimType = useCallback(
    (v: string) => setConfig((prev) => ({ ...prev, claimType: v })),
    [setConfig],
  );
  const setJurisdiction = useCallback(
    (v: string) => setConfig((prev) => ({ ...prev, jurisdiction: v })),
    [setConfig],
  );
  const setHelpfulMode = useCallback(
    (v: boolean) => setConfig((prev) => ({ ...prev, helpfulMode: v })),
    [setConfig],
  );
  const setCat3Toggle = useCallback(
    (v: boolean) => setConfig((prev) => ({ ...prev, cat3Toggle: v })),
    [setConfig],
  );
  const setAddress = useCallback(
    (v: string) => setConfig((prev) => ({ ...prev, address: v })),
    [setConfig],
  );

  // --- Admin: pipeline trace toggles (transient — admin-only, not persisted) ---
  // Reason (PRP-009): backend gates the debug payload on admin role, so
  // non-admins never see these toggles and never receive trace data even
  // if they monkeypatch their requests. The env-var fallback
  // (DEV_SCOPE_DEBUG=1) remains for local dev / CI but is no longer
  // required when running as an admin user.
  const isAdmin = useIsAdmin();
  const [debugTrace, setDebugTrace] = useState(false);
  const [stepMode, setStepMode] = useState(false);
  // Reason: Force toggles off if the user lost admin between renders
  // (logout / role revoke). Prevents stale state from sending debug_trace
  // after the user is no longer entitled to it.
  useEffect(() => {
    if (!isAdmin) {
      setDebugTrace(false);
      setStepMode(false);
    }
  }, [isAdmin]);

  // --- Copy-as-markdown toast state (transient UI feedback, not persisted) ---
  const [markdownCopied, setMarkdownCopied] = useState<'idle' | 'ok' | 'error'>(
    'idle',
  );

  // --- Project context (re-fetched from API, not persisted) ---
  const [projectName, setProjectName] = useState<string | null>(null);
  const [savedToProject, setSavedToProject] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // --- Room entries (persisted) ---
  const [rooms, setRooms] = useSessionState<RoomEntry[]>(roomsKey, () => [
    { id: generateId(), room_type: '', room_type_label: '', description: '' },
  ]);

  // --- Scope output (persisted — primary fix for the navigate-away bug) ---
  const [accumulatedScope, setAccumulatedScope] =
    useSessionState<ScopeGenerationResponse | null>(scopeKey, null);

  const handleCopyMarkdown = useCallback(async () => {
    if (!accumulatedScope) return;
    const markdown = scopeToMarkdown(accumulatedScope);
    try {
      await navigator.clipboard.writeText(markdown);
      setMarkdownCopied('ok');
    } catch {
      setMarkdownCopied('error');
    }
    setTimeout(() => setMarkdownCopied('idle'), 2000);
  }, [accumulatedScope]);

  // --- Mode: 'generate' (fresh scope) or 'supplement' (find missing items) ---
  const [mode, setMode] = useState<'generate' | 'supplement'>('generate');

  // --- Supplement flow state ---
  const [supplementNotes, setSupplementNotes] = useState('');
  const [supplementScopeId, setSupplementScopeId] = useState<string | null>(null);
  const [isSupplementing, setIsSupplementing] = useState(false);
  const [supplementResult, setSupplementResult] = useState<ScopeGenerationResponse | null>(null);
  const [supplementError, setSupplementError] = useState<string | null>(null);

  // Load latest scope ID from project when in supplement mode
  useEffect(() => {
    if (mode !== 'supplement' || !projectId) return;
    BackendAPI.getLatestProjectScope(projectId)
      .then((result) => {
        if (result?.id) setSupplementScopeId(result.id as string);
      })
      .catch(() => setSupplementScopeId(null));
  }, [mode, projectId]);

  const handleSupplement = async () => {
    if (!supplementScopeId || !supplementNotes.trim()) return;
    setIsSupplementing(true);
    setSupplementError(null);
    setSupplementResult(null);
    try {
      const result = await BackendAPI.supplementScope({
        scope_id: supplementScopeId,
        notes: supplementNotes.trim(),
        damage_type: damageType || undefined,
        jurisdiction: jurisdiction || undefined,
        property_type: claimType || undefined,
        cat3_toggle: cat3Toggle || undefined,
      });
      setSupplementResult(result);
    } catch (err) {
      setSupplementError(err instanceof Error ? err.message : 'Supplement failed');
    } finally {
      setIsSupplementing(false);
    }
  };

  // --- Batch generation ---
  const [isGeneratingBatch, setIsGeneratingBatch] = useState(false);
  // Live pipeline stage from the SSE stream (null until the first arrives).
  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [batchError, setBatchError] = useState<string | null>(null);

  // --- Inline "save as new project + generate" modal (only used when no project) ---
  const [showCreateAndGenerate, setShowCreateAndGenerate] = useState(false);
  // Reason: After creating a project via the inline modal we navigate to
  // `?project=<id>`. The page doesn't unmount on a query-only navigate,
  // so we flip this flag right before navigating; an effect then fires
  // batch generation once projectId reflects the new value.
  const [pendingAutoGenerate, setPendingAutoGenerate] = useState(false);

  // Reason: Load project context when URL has ?project=<id>. Pre-fill any
  // empty config fields from the project record so the user doesn't have
  // to re-enter address/ZIP/damage/claim they already provided when
  // creating the project. Never clobber a user-edited value — only set
  // when the current config field is empty.
  useEffect(() => {
    if (!projectId) return;
    BackendAPI.getProject(projectId)
      .then((project) => {
        setProjectName(project.owner_name || project.address || null);
        setConfig((prev) => {
          const next = { ...prev };
          if (!prev.address && project.address) next.address = project.address;
          if (!prev.jurisdiction && project.zip) next.jurisdiction = project.zip;
          if (!prev.damageType && project.damage_type) next.damageType = project.damage_type;
          if (!prev.claimType && project.claim_type) next.claimType = project.claim_type;
          return next;
        });
      })
      .catch((err) => {
        console.warn('[ScopeBuilder] Failed to load project context:', err);
      });
    // Reason: depend only on projectId; setConfig from useSessionState is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Prune stale per-project sessionStorage on mount: keys whose UUID
  // doesn't correspond to a current project (project was deleted) get
  // dropped. Also purges the legacy `__no_project__` bucket from the
  // pre-cleanup shape.
  useEffect(() => {
    const run = async () => {
      let current = useProjectStore.getState().projects;
      if (current.length === 0) {
        try {
          await useProjectStore.getState().fetchProjects();
          current = useProjectStore.getState().projects;
        } catch {
          return;
        }
      }
      const live = new Set(current.map((p) => p.id));
      const toDrop: string[] = [];
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const k = window.sessionStorage.key(i);
        if (!k) continue;
        if (k.startsWith('scopeBuilder:')) {
          const parts = k.split(':');
          const tail = parts[parts.length - 1];
          if (tail === '__no_project__' || (tail && !live.has(tail))) {
            toDrop.push(k);
          }
        }
      }
      for (const k of toDrop) window.sessionStorage.removeItem(k);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handles address selection from autocomplete.
   * Auto-fills the ZIP code field for jurisdiction lookup.
   *
   * Args:
   *   structured (StructuredAddress): Parsed address components.
   */
  const handleAddressSelect = useCallback((structured: StructuredAddress) => {
    setAddress(structured.formatted || structured.street);
    // Reason: Auto-fill ZIP code for jurisdiction-based building code requirements
    if (structured.zip) {
      setJurisdiction(structured.zip);
    }
  }, []);

  /**
   * Auto-saves generated scope to the project if project context exists.
   *
   * Args:
   *   scopeData (ScopeGenerationResponse): The generated scope to save.
   */
  const autoSaveToProject = useCallback(
    async (scopeData: ScopeGenerationResponse) => {
      if (!projectId) return;
      setSavedToProject(false);
      setSaveError(null);
      try {
        // PRP-003 — echo rule_trace to the save endpoint so per-fire events
        // get persisted into scope_rule_traces alongside the scope.
        await BackendAPI.saveProjectScope(
          projectId,
          scopeData,
          'web',
          scopeData.rule_trace,
        );
        setSavedToProject(true);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save scope';
        console.error('[ScopeBuilder] Failed to auto-save scope:', message);
        setSaveError(message);
      }
    },
    [projectId]
  );

  // --- Room update handler ---
  const handleRoomUpdate = useCallback((updated: RoomEntry) => {
    setRooms((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  }, []);

  // --- Add room ---
  const handleAddRoom = useCallback(() => {
    setRooms((prev) => [
      ...prev,
      { id: generateId(), room_type: '', room_type_label: '', description: '' },
    ]);
  }, []);

  // --- Remove room ---
  const handleRemoveRoom = useCallback((roomId: string) => {
    setRooms((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((r) => r.id !== roomId);
    });
  }, []);

  // --- Per-room generation ---
  const handleGenerateRoom = useCallback(
    async (entry: RoomEntry) => {
      if (!damageType || !entry.description.trim()) return;

      // Mark room as generating
      setRooms((prev) =>
        prev.map((r) =>
          r.id === entry.id ? { ...r, generating: true, error: undefined } : r
        )
      );

      const request: RoomScopeRequest = {
        room_description: entry.description,
        damage_type: damageType,
        ...(entry.room_type && { room_type: entry.room_type }),
        ...(claimType && { claim_type: claimType }),
        ...(jurisdiction && { jurisdiction }),
        ...(entry.measurements && { measurements: entry.measurements }),
        helpful_mode: helpfulMode,
        cat3_toggle: cat3Toggle,
        ...(debugTrace && { debug_trace: true }),
      };

      try {
        const result = await BackendAPI.generateRoomScope(request);

        // Update room with result
        setRooms((prev) =>
          prev.map((r) =>
            r.id === entry.id
              ? {
                  ...r,
                  generating: false,
                  scope: result.room ?? undefined,
                  checklist_prompts: result.checklist_prompts,
                  error: undefined,
                }
              : r
          )
        );

        // Merge into accumulated scope and auto-save
        if (result.room) {
          setAccumulatedScope((prev) => {
            const merged = mergeRoomIntoScope(prev, result.room!, result.validation_issues, result.questions);
            // Reason: Auto-save after merge completes
            autoSaveToProject(merged);
            return merged;
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to generate scope for this room';
        setRooms((prev) =>
          prev.map((r) =>
            r.id === entry.id ? { ...r, generating: false, error: message } : r
          )
        );
      }
    },
    [damageType, claimType, jurisdiction, helpfulMode, cat3Toggle, debugTrace, autoSaveToProject]
  );

  // --- Batch generation ---
  // Reason: `skipSave` lets the Scope Builder generate a scope without
  // attaching it to a project (the "quick test" workflow when no
  // ?project=<id> is in the URL).
  const handleBatchGenerate = useCallback(async (opts?: { skipSave?: boolean }) => {
    if (!damageType) return;

    const roomsWithContent = rooms.filter((r) => r.description.trim().length > 0);
    if (roomsWithContent.length === 0) return;

    const skipSave = opts?.skipSave ?? false;

    setIsGeneratingBatch(true);
    setBatchError(null);
    setCurrentStage(null);
    setElapsedSeconds(0);

    // Elapsed-time ticker for the progress card.
    const startedAt = Date.now();
    const elapsedInterval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    // S3: Send structured room_sections so each room's notes route to only that room.
    // field_notes carries the house-details overview (if any).
    const roomSections = roomsWithContent.map((r) => ({
      room: r.room_type_label || r.room_type || 'Room',
      text: r.description,
    }));

    // Fallback overview: if no houseDetails, use a minimal summary for fact_extractor
    const overview = houseDetails || '';

    const requestPayload = {
      field_notes: overview,
      room_sections: roomSections,
      damage_type: damageType,
      ...(claimType && { property_type: claimType }),
      ...(jurisdiction && { jurisdiction }),
      cat3_toggle: cat3Toggle,
      ...(helpfulMode && { helpful_mode: true }),
      ...(debugTrace && { debug_trace: true }),
    };

    try {
      // Prefer the streaming endpoint for live stage progress; fall back to
      // the single-shot call if the stream can't be established so generation
      // never hard-fails on a transport hiccup.
      let result: ScopeGenerationResponse;
      try {
        result = await streamScopeGeneration(requestPayload, setCurrentStage);
      } catch (streamErr) {
        console.warn('Scope stream failed, falling back to non-streaming:', streamErr);
        result = await BackendAPI.generateScopeEnhanced(requestPayload);
      }

      setAccumulatedScope(result);

      // Reason: Auto-save batch results to project unless explicitly
      // skipped (e.g. the "Generate scope without saving" link when
      // no project is selected).
      if (!skipSave) {
        autoSaveToProject(result);
      }

      // Update rooms with their matched scope results
      setRooms((prev) =>
        prev.map((r) => {
          const matchedRoom = result.rooms.find(
            (sr) =>
              sr.room_type?.toLowerCase() === r.room_type?.toLowerCase() ||
              sr.room_name?.toLowerCase().includes(r.room_type_label?.toLowerCase() || '---')
          );
          if (matchedRoom) {
            return { ...r, scope: matchedRoom, generating: false, error: undefined };
          }
          return { ...r, generating: false };
        })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Batch generation failed';
      setBatchError(message);
    } finally {
      clearInterval(elapsedInterval);
      setIsGeneratingBatch(false);
      setCurrentStage(null);
    }
  }, [damageType, claimType, jurisdiction, cat3Toggle, helpfulMode, debugTrace, rooms, autoSaveToProject]);

  // Reason: When the "save as new project and generate" flow creates a
  // project, we set pendingAutoGenerate and navigate to ?project=<id>.
  // Once projectId reflects that change on the next render, fire the
  // batch generation (which will then auto-save to the new project).
  useEffect(() => {
    if (pendingAutoGenerate && projectId) {
      setPendingAutoGenerate(false);
      handleBatchGenerate();
    }
  }, [pendingAutoGenerate, projectId, handleBatchGenerate]);

  // --- Derived state ---
  const canGenerate = damageType.length > 0;
  const hasContent = rooms.some((r) => r.description.trim().length > 0);
  const completedRoomCount = rooms.filter((r) => r.scope).length;
  const anyGenerating = rooms.some((r) => r.generating) || isGeneratingBatch;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm font-medium text-muted-foreground">
        <Link to="/" className="hover:text-primary">
          Dashboard
        </Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="text-foreground">Scope Builder</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Scope Builder</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Describe damage room-by-room or all at once. Use your device's
          dictation to speak into any text area.
        </p>
      </div>

      {/* Project context banner */}
      {projectId && projectName && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-2">
          <span className="text-sm text-blue-800">
            Project: <span className="font-medium">{projectName}</span>
          </span>
          <span className="text-xs text-blue-600">
            (scope will auto-save to this project)
          </span>
        </div>
      )}

      {/* No project tip */}
      {!projectId && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-2">
          <p className="text-xs text-muted-foreground">
            Tip: Create a project first to automatically save your scope.
          </p>
        </div>
      )}

      {/* Mode tabs — Supplement only available when a project with a saved scope exists */}
      {projectId && (
        <div className="flex gap-1 rounded-lg border border-border bg-muted p-1 w-fit">
          <button
            type="button"
            onClick={() => setMode('generate')}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              mode === 'generate'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Generate New Scope
          </button>
          <button
            type="button"
            onClick={() => setMode('supplement')}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              mode === 'supplement'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Supplement Existing Scope
          </button>
        </div>
      )}

      {/* Supplement mode UI */}
      {mode === 'supplement' && projectId && (
        <div className="space-y-4 rounded-lg border bg-card p-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">Find Missing Items</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Describe what you found during re-inspection or what the original estimator missed.
              The engine will compare against your existing scope and show only the <em>delta</em> — items not already there.
            </p>
          </div>

          {!supplementScopeId && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-sm text-amber-800">
                No saved scope found for this project yet. Generate a scope first, then supplement it.
              </p>
            </div>
          )}

          <textarea
            value={supplementNotes}
            onChange={(e) => setSupplementNotes(e.target.value)}
            placeholder="e.g. The living room also had water damage behind the drywall — mold present. Master bath vanity needs replacement. Attic insulation was saturated."
            rows={5}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y"
            disabled={isSupplementing}
          />

          <div className="flex items-center gap-3">
            <Button
              onClick={handleSupplement}
              disabled={!supplementScopeId || !supplementNotes.trim() || isSupplementing}
              variant="primary"
            >
              {isSupplementing ? (
                <>
                  <Loader2 className="-ml-1 mr-2 h-4 w-4 animate-spin" />
                  Finding missing items…
                </>
              ) : (
                <>
                  <Zap className="-ml-1 mr-2 h-4 w-4" />
                  Find Missing Items
                </>
              )}
            </Button>
          </div>

          {supplementError && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
              <p className="text-sm text-red-800">{supplementError}</p>
            </div>
          )}

          {supplementResult && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">
                  {supplementResult.rooms.reduce((n, r) => n + r.items.length, 0) +
                    supplementResult.general_conditions.length} missing item(s) found
                </span>
              </div>
              {supplementResult.warnings.map((w, i) => (
                <p key={i} className="text-xs text-muted-foreground">{w}</p>
              ))}
              <ScopeOutput scope={supplementResult} />
            </div>
          )}
        </div>
      )}

      {/* Address autocomplete + configuration bar */}
      <div className={`space-y-4 rounded-lg border bg-card p-4 ${mode === 'supplement' ? 'hidden' : ''}`}>
        {/* Address field */}
        <div className="space-y-2">
          <label htmlFor="scope-address" className="text-sm font-medium text-foreground">
            Property Address
          </label>
          <AddressAutocomplete
            onSelect={handleAddressSelect}
            defaultValue={address}
            placeholder="Start typing an address to auto-fill ZIP..."
          />
          <p className="text-xs text-muted-foreground">
            Selecting an address auto-fills the ZIP code for jurisdiction lookup.
          </p>
        </div>

        {/* House Details (S3: overall-house box) */}
        <div className="space-y-1.5">
          <label htmlFor="house-details" className="text-sm font-medium text-foreground">
            House Details <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <textarea
            id="house-details"
            value={houseDetails}
            onChange={(e) => setHouseDetails(e.target.value)}
            placeholder="Overall property details, e.g. '3-coat plaster throughout, hardwood floors, 1950s construction, stucco exterior'"
            rows={2}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
          <p className="text-xs text-muted-foreground">
            Material defaults (wall substrate, flooring, era) apply to every room as a baseline.
          </p>
        </div>

        {/* Damage type / claim type / ZIP */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[300px]">
            <DamageTypeSelector
              damageType={damageType}
              onDamageTypeChange={(val) => {
                setDamageType(val);
                // Reason: Flood damage is almost always an NFIP claim — auto-set claim type
                if (val === 'flood' && !claimType) {
                  setClaimType('flood');
                }
              }}
              claimType={claimType}
              onClaimTypeChange={setClaimType}
              jurisdiction={jurisdiction}
              onJurisdictionChange={setJurisdiction}
              cat3Toggle={cat3Toggle}
              onCat3ToggleChange={setCat3Toggle}
            />
          </div>
          <div className="flex items-center gap-4">
            {/* Helpful mode toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={helpfulMode}
                onChange={(e) => setHelpfulMode(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="font-medium text-foreground">Helpful Mode</span>
            </label>

            {/* Admin-only debug toggles. PRP-009 gates these on the
                `admin` role so non-admin users never see them. The
                backend also rechecks the role before emitting any
                debug payload. */}
            {isAdmin && (
              <>
                <label
                  className="flex items-center gap-2 text-sm cursor-pointer select-none"
                  title="Admin only — surfaces full pipeline trace including LLM prompts/responses"
                >
                  <input
                    type="checkbox"
                    checked={debugTrace}
                    onChange={(e) => {
                      setDebugTrace(e.target.checked);
                      if (!e.target.checked) setStepMode(false);
                    }}
                    className="h-4 w-4 rounded border-purple-300 text-purple-700 focus:ring-purple-500"
                  />
                  <span className="font-medium text-purple-800">Show pipeline trace</span>
                </label>

                {/* Step mode — only meaningful when trace is on */}
                {debugTrace && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={stepMode}
                      onChange={(e) => setStepMode(e.target.checked)}
                      className="h-4 w-4 rounded border-purple-300 text-purple-700 focus:ring-purple-500"
                    />
                    <span className="font-medium text-purple-800">Step mode</span>
                  </label>
                )}
              </>
            )}

            {/* Batch generate controls. When no project is selected, we
                offer a primary "save as new project and generate" CTA
                plus a secondary "generate without saving" link. With a
                project selected, this is the existing batch button. */}
            {projectId ? (
              <Button
                onClick={() => handleBatchGenerate()}
                disabled={!canGenerate || !hasContent || anyGenerating}
                variant="primary"
              >
                <Zap className="-ml-1 mr-2 h-4 w-4" />
                Batch Generate
              </Button>
            ) : (
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setShowCreateAndGenerate(true)}
                  disabled={!canGenerate || !hasContent || anyGenerating}
                  variant="primary"
                >
                  <Zap className="-ml-1 mr-2 h-4 w-4" />
                  Save as new project and generate scope
                </Button>
                <button
                  type="button"
                  onClick={() => handleBatchGenerate({ skipSave: true })}
                  disabled={!canGenerate || !hasContent || anyGenerating}
                  className="text-sm font-medium text-primary underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Generate scope without saving to a project
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Batch error (only in generate mode) */}
      {mode === 'generate' && batchError && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
          <div>
            <p className="text-sm font-medium text-red-800">Batch generation failed</p>
            <p className="text-sm text-red-700">{batchError}</p>
            <button
              onClick={() => handleBatchGenerate({ skipSave: !projectId })}
              className="mt-1 text-sm font-medium text-red-800 underline hover:text-red-900"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Batch progress overlay — live stages from the SSE stream (generate mode only) */}
      {mode === 'generate' && isGeneratingBatch && (
        <GenerationProgress currentStage={currentStage} elapsedSeconds={elapsedSeconds} />
      )}

      {/* Room input area (generate mode only) */}
      {mode === 'generate' && !isGeneratingBatch && (
        <div className="space-y-4">
          {rooms.map((room, index) => (
            <div key={room.id} className="space-y-2">
              {/* Room panel header with status badge */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Room {index + 1}
                </span>
                {room.scope && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    <CheckCircle className="h-3 w-3" />
                    Generated
                  </span>
                )}
                {rooms.length > 1 && !room.generating && (
                  <button
                    onClick={() => handleRemoveRoom(room.id)}
                    className="ml-auto text-xs text-muted-foreground hover:text-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* Room input panel */}
              <RoomInputPanel
                roomEntry={room}
                onUpdate={handleRoomUpdate}
                onGenerate={handleGenerateRoom}
                isGenerating={room.generating ?? false}
              />

              {/* Per-room error */}
              {room.error && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
                  <div>
                    <p className="text-sm text-red-800">{room.error}</p>
                    <button
                      onClick={() => handleGenerateRoom(room)}
                      className="mt-1 text-sm font-medium text-red-800 underline hover:text-red-900"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}

              {/* Per-room generation progress */}
              {room.generating && (
                <div className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 p-3">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm text-blue-800">
                    Generating scope for {room.room_type_label || 'this room'}...
                  </span>
                </div>
              )}

              {/* Helpful mode checklist (when enabled + description is long enough) */}
              {helpfulMode &&
                room.room_type &&
                room.description.length > 20 &&
                !room.scope && (
                  <HelpfulModeChecklist
                    roomType={room.room_type}
                    description={room.description}
                  />
                )}
            </div>
          ))}

          {/* Add another room button */}
          <Button
            variant="outline"
            onClick={handleAddRoom}
            className="w-full border-dashed"
          >
            <Plus className="-ml-1 mr-2 h-4 w-4" />
            Add Another Room
          </Button>

          {/* Damage type reminder */}
          {!canGenerate && (
            <p className="text-center text-sm text-amber-600">
              Select a damage type above before generating scope.
            </p>
          )}
        </div>
      )}

      {/* Scope output area (generate mode only) */}
      {mode === 'generate' && accumulatedScope && accumulatedScope.rooms.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">
                Scope generated{' '}
                {completedRoomCount > 0 && `(${completedRoomCount} room${completedRoomCount !== 1 ? 's' : ''})`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Auto-save status indicator */}
              {savedToProject && (
                <span className="inline-flex items-center gap-1 text-xs text-green-700">
                  <Save className="h-3.5 w-3.5" />
                  Saved to project
                </span>
              )}
              {saveError && (
                <span className="text-xs text-red-600">Save failed</span>
              )}

              {/* Copy as markdown */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyMarkdown}
                title="Copy the entire scope as a markdown document"
              >
                {markdownCopied === 'ok' ? (
                  <CheckIcon className="-ml-1 mr-1.5 h-3.5 w-3.5 text-green-600" />
                ) : (
                  <ClipboardCopy className="-ml-1 mr-1.5 h-3.5 w-3.5" />
                )}
                {markdownCopied === 'ok'
                  ? 'Copied!'
                  : markdownCopied === 'error'
                    ? 'Copy failed'
                    : 'Copy as markdown'}
              </Button>

              {/* View in Review button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (projectId) {
                    navigate(`/scope/review?project=${projectId}`);
                  } else {
                    // Reason: Pass scope data via React Router state when no project context
                    navigate('/scope/review', { state: { scopeData: accumulatedScope } });
                  }
                }}
              >
                <ExternalLink className="-ml-1 mr-1.5 h-3.5 w-3.5" />
                View in Review
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAccumulatedScope(null);
                  setSavedToProject(false);
                  setSaveError(null);
                  setRooms((prev) =>
                    prev.map((r) => ({
                      ...r,
                      scope: undefined,
                      checklist_prompts: undefined,
                      error: undefined,
                    }))
                  );
                }}
              >
                Clear Results
              </Button>
            </div>
          </div>
          {/* Admin debug: top-of-scope pipeline trace summary. Renders
              only when the admin opted into debug_trace AND the backend
              returned a pipeline_trace block. PRP-009 acceptance #1/2. */}
          {debugTrace && isAdmin && (
            <PipelineTracePanel scope={accumulatedScope} stepMode={stepMode} />
          )}
          <ScopeOutput
            scope={accumulatedScope}
            showDebug={debugTrace}
            stepMode={stepMode}
          />
        </div>
      )}

      {showCreateAndGenerate && (
        <CreateProjectModal
          title="Save as new project and generate scope"
          subtitle="The project will be created and scope generation will run immediately."
          submitLabel="Create project and generate"
          initialValues={{ zip: jurisdiction }}
          onClose={() => setShowCreateAndGenerate(false)}
          onCreated={(project) => {
            setShowCreateAndGenerate(false);
            setPendingAutoGenerate(true);
            // Scratchpad → pinned: seed the new project's sessionStorage
            // bucket with the work-in-progress config/rooms BEFORE the
            // URL change. Otherwise useSessionState re-keys to the empty
            // bucket and the user loses their inputs mid-flow.
            try {
              window.sessionStorage.setItem(
                `scopeBuilder:config:${project.id}`,
                JSON.stringify(config),
              );
              window.sessionStorage.setItem(
                `scopeBuilder:rooms:${project.id}`,
                JSON.stringify(rooms),
              );
              if (accumulatedScope !== null) {
                window.sessionStorage.setItem(
                  `scopeBuilder:scope:${project.id}`,
                  JSON.stringify(accumulatedScope),
                );
              }
            } catch {
              // Quota / serialization failure: not fatal — state is still
              // in memory and the modal navigates regardless.
            }
            // Reason: URL is source of truth for the active project.
            // Navigating here updates ?project=<id>; the effect above
            // then fires batch generation on the next render.
            navigate(`/scope/builder?project=${project.id}`);
          }}
        />
      )}
    </div>
  );
}

// ================================================================= //
//          Helper: Merge a single room result into accumulated scope  //
// ================================================================= //

/**
 * Merge a per-room scope result into the accumulated ScopeGenerationResponse.
 * Creates a new response if none exists, or appends the room and merges issues.
 *
 * Args:
 *   prev: The existing accumulated scope (or null).
 *   room: The new room to add.
 *   validationIssues: Validation issues from this room's generation.
 *   questions: Questions from this room's generation.
 *
 * Returns:
 *   ScopeGenerationResponse: The merged scope.
 */
function mergeRoomIntoScope(
  prev: ScopeGenerationResponse | null,
  room: ScopeResponseRoom,
  validationIssues: ValidationIssue[],
  questions: string[]
): ScopeGenerationResponse {
  if (!prev) {
    return {
      rooms: [room],
      general_conditions: [],
      code_requirements: [],
      validation_issues: validationIssues || [],
      questions: questions || [],
      warnings: [],
      metadata: {},
      stats: { rooms_generated: 1 },
    };
  }

  // Check if room already exists (by name) and replace, else append
  const existingIndex = prev.rooms.findIndex(
    (r) => r.room_name.toLowerCase() === room.room_name.toLowerCase()
  );

  const updatedRooms =
    existingIndex >= 0
      ? prev.rooms.map((r, i) => (i === existingIndex ? room : r))
      : [...prev.rooms, room];

  // Merge validation issues (deduplicate by message)
  const existingMessages = new Set(prev.validation_issues.map((v) => v.message));
  const newIssues = (validationIssues || []).filter(
    (v) => !existingMessages.has(v.message)
  );

  // Merge questions (deduplicate)
  const existingQuestions = new Set(prev.questions);
  const newQuestions = (questions || []).filter((q) => !existingQuestions.has(q));

  return {
    ...prev,
    rooms: updatedRooms,
    validation_issues: [...prev.validation_issues, ...newIssues],
    questions: [...prev.questions, ...newQuestions],
    stats: { ...prev.stats, rooms_generated: updatedRooms.length },
  };
}
